import cpu from '../../../assets/svg-icons/blueCPU.svg';
import ram from '../../../assets/svg-icons/ram.svg';
import hdd from '../../../assets/svg-icons/disk.svg';
import { useState, useEffect, useContext, useRef } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import closeIcon from '../../../assets/svg-icons/closeBrand.svg';
import Input from '../../../components/input';
import Select from '../../../components/select';
import ubuntu from '../../../assets/svg-icons/Ubuntu logo.svg';
import debian from '../../../assets/svg-icons/debian logo.svg';
import rocky from '../../../assets/svg-icons/rocky logo.svg';
import centOS from '../../../assets/svg-icons/centOS logo.svg';
import openSUSE from '../../../assets/svg-icons/openSUSE logo.svg';
import fedora from '../../../assets/svg-icons/fedora logo.svg';
import windows from '../../../assets/images/windows.png';
import oracle from '../../../assets/svg-icons/oracle-logo.svg';
import close from '../../../assets/svg-icons/closeBrand.svg';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  listImages,
  listKeypairs,
  createKeypair,
  createServer,
  getResourceStatus,
  listServers,
  listPrivateNetworks,
  getUserVirtualAccount,
  getCreditValidator,
  createPaymentLink,
} from '../../../hooks/local/userReducer';
import { CartContext } from '../../../components/cartContext';
import {
  extractDiskSize,
  useListLocalImageDetails,
  useNetworkList,
  useServerList,
} from '../layout/resuableEffect';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Spinner from '../../../components/spinner';
import { toast } from 'react-toastify';
import { scroller } from 'react-scroll';
import { IoCopyOutline } from 'react-icons/io5';
import usePartnerNavigation from '../../../hooks/usePartnerNavigation';

const ConfigureVPS = () => {

  const { navigate, generatePartnerUrl } = usePartnerNavigation();

  const dispatch = useDispatch();
  const location = useLocation();

  const isLoading = useSelector((state) => state.user.loading);
  const { images = {} } = useSelector((state) => state.user);
  const { keypairs = {} } = useSelector((state) => state.user);
  const { totalPrice, setTotalPrice, currency, duration, setDuration } =
    useContext(CartContext);
  const walletBalance = useSelector((state) =>
    state.user.virtualAccount
      ? currency === 'USD'
        ? state.user.virtualAccount.dollar_balance
        : state.user.virtualAccount.balance
      : 0,
  );
  const virtualAccount = useSelector((state) => state.user.virtualAccount);

  const [cartItems, setCartItems] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedOption, setSelectedOption] = useState('existing');
  const [authMethod, setAuthMethod] = useState('keypairs');
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successDetails, setSuccessDetails] = useState({});
  const [extraVolumeSize, setExtraVolumeSize] = useState('');
  const [extraEgressSize, setExtraEgressSize] = useState('');
  const [networkType, setNetworkType] = useState('public');
  const [selectedPrivateNetwork, setSelectedPrivateNetwork] = useState('');
  const [privateNetworks, setPrivateNetworks] = useState([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  const [showWalletModal, setShowWalletModal] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [confirmPaymentLoading, setConfirmPaymentLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [paymentLink, setPaymentLink] = useState('');
  const [linkGenerated, setLinkGenerated] = useState(false);
  const [amountValue, setAmountValue] = useState('');

  const userServer = useServerList();
  const localImageDetails = useListLocalImageDetails();

  useEffect(() => {
    if (location.pathname === '/client-area/configure-vps') {
      const snapshot = sessionStorage.getItem('selectedServerSnapshot');
      if (snapshot) {
        const parsedSnapshot = JSON.parse(snapshot);
        const snapshotSize = parseFloat(
          parsedSnapshot.snapshotSize.replace(/ GB$/, ''),
        );
        const updatedSnapshot = { ...parsedSnapshot, snapshotSize };
        setSelectedSnapshot(updatedSnapshot);

        if (updatedSnapshot.action === 'launch') {
          setSelectedImage('snapshot');
          setSelectedImageRef(updatedSnapshot.snapshotId);
          setAuthMethod('keypairs');
          createNewServer.setFieldValue('imageRef', updatedSnapshot.snapshotId);
          createNewServer.setFieldValue('imagesRef', 'snapshot');

          const defaultUsername = getDefaultUsername(
            updatedSnapshot.snapshotOS,
          );
          createNewServer.setFieldValue('username', defaultUsername);
        }
      }
    }

    return () => {
      if (location.pathname !== '/client-area/configure-vps') {
        sessionStorage.removeItem('selectedServerSnapshot');
        setSelectedSnapshot(null);
      }
    };
  }, [location.pathname]);

  // Helper function to determine default username based on OS distro
  const getDefaultUsername = (osDistro) => {
    switch (osDistro?.toLowerCase()) {
      case 'ubuntu':
        return 'ubuntu';
      case 'centos':
        return 'centos';
      case 'debian':
        return 'debian';
      case 'rocky':
        return 'rocky';
      case 'opensuse':
        return 'opensuse';
      case 'fedora':
        return 'fedora';
      case 'oracle':
        return 'oracle';
      default:
        return '';
    }
  };

  const userServerList = userServer
    ?.filter((server) => server.addresses)
    .map((server) => {
      const externalAddress = server.addresses?.External?.[0]?.addr;
      const allAddresses = Object.values(server.addresses)
        .flat()
        .map((address) => address.addr)
        .join(', ');

      return {
        serverId: server?.id,
        name: server.name.replace(/_/g, ' '),
        address: externalAddress || allAddresses || '',
        status: server.status === 'ACTIVE' ? 'Active' : 'Inactive',
      };
    });

  const errorRefs = {
    name: useRef(null),
    imagesRef: useRef(null),
    imageRef: useRef(null),
    old_key_pair_name: useRef(null),
    volume_size: useRef(null),
    free_egress: useRef(null),
    keypair_name: useRef(null),
    username: useRef(null),
    user_pass: useRef(null),
    confirm_pass: useRef(null),
    private_network: useRef(null),
  };

  const scrollContainerRef = useRef(null);

  const publicNetworkId = '5236d605-3714-41f8-b1c1-6247d3f15cdb';
  const flavorRef = cartItems?.[0]?.productId;
  const flavorId = cartItems?.[0]?.flavorId;
  const storage = cartItems?.[0]?.diskSize;
  const freeEgress = cartItems?.[0]?.egressSize;

  const extraStorage = extractDiskSize(storage);

  const selectedProjectId = useSelector(
    (state) => state.user.selectedProject.id,
  );

  const networkList = useNetworkList(selectedProjectId);

  useEffect(() => {
    if (networkType === 'private') {
      const fetchPrivateNetworks = () => {
        setPrivateNetworks(networkList);
      };
      fetchPrivateNetworks();
    }
  }, [networkType, dispatch, selectedProjectId]);

  useEffect(() => {
    const cart = sessionStorage.getItem('cart');
    if (cart) {
      const parsedCart = JSON.parse(cart);
      setCartItems(parsedCart);
      const initialPrice = parsedCart[0]?.price || 0;
      setTotalPrice(initialPrice);
      setDuration(parsedCart[0]?.paymentMethod || 'Monthly');
    } else {
      setCartItems([]);
      setTotalPrice(0);
    }
  }, [setTotalPrice, setDuration]);

  const calculateExtraVolumeCost = (size, currency) => {
    const pricePerGB = currency === 'USD' ? 0.2 : 300;
    return size ? parseFloat(size) * pricePerGB : 0;
  };

  const calculateExtraEgressCost = (size, currency) => {
    // Get egress pricing from cart item if available, otherwise use default pricing
    const egressPricePerGB = cartItems[0]?.egressPrice
      ? parseFloat(cartItems[0].egressPrice.replace(/NGN|USD|\$|,/g, ''))
      : (currency === 'USD' ? 0.4 : 500);
    return size ? parseFloat(size) * egressPricePerGB : 0;
  };

  const calculateTotalPrice = (
    cartItems,
    duration,
    extraVolumeSize,
    extraEgressSize,
  ) => {
    let totalCartPrice = 0;
    cartItems.forEach((item) => {
      let priceString = '';
      if (currency === 'USD') {
        priceString = item.dollarPrice;
      } else if (currency === 'NGN') {
        priceString = item.nairaPrice;
      }
      const price = parseFloat(
        priceString
          ? priceString
            .replace(/NGN|USD|\$/g, '')
            .replace(/,/g, '')
            .trim()
          : item.price || 0,
      );
      totalCartPrice += price;
    });

    if (duration === 'Yearly') {
      const yearlyPrice = totalCartPrice * 12;
      const discount = yearlyPrice * 0.01;
      totalCartPrice = yearlyPrice - discount;
    }

    const extraVolumeCost = calculateExtraVolumeCost(extraVolumeSize, currency);
    const extraEgressCost = calculateExtraEgressCost(extraEgressSize, currency);
    return totalCartPrice + extraVolumeCost + extraEgressCost;
  };

  useEffect(() => {
    const newTotalPrice = calculateTotalPrice(
      cartItems,
      duration,
      extraVolumeSize,
      extraEgressSize,
    );
    setTotalPrice(newTotalPrice);
  }, [
    cartItems,
    duration,
    extraVolumeSize,
    extraEgressSize,
    currency,
    setTotalPrice,
  ]);

  const removeFromCart = () => {
    sessionStorage.removeItem('cart');
    setCartItems([]);
    setTotalPrice(0);
    window.location.reload();
  };

  useEffect(() => {
    dispatch(listImages());
  }, [dispatch]);

  useEffect(() => {
    dispatch(listKeypairs(selectedProjectId));
  }, [dispatch, selectedProjectId]);

  const handleSelect = (duration) => {
    setDuration(duration);
  };

  useEffect(() => {
    if (cartItems.length > 0 && cartItems[0].paymentMethod) {
      handleSelect(cartItems[0].paymentMethod);
    }
  }, [cartItems]);

  const activeStyle =
    'text-sm bg-primaryBlue/10 rounded px-5 md:px-10 h-14 flex items-center border border-primaryBlue cursor-pointer';
  const inactiveStyle =
    'text-sm bg-[#c4c4c416] rounded px-5 md:px-10 h-14 flex items-center border dark:border-[#c4c4c424] cursor-pointer';

  const formatOptions = (data = []) => {
    return data.map((item) => ({
      value: item?.id,
      label: `${item?.os_distro ? `${item?.os_distro} - ` : ''}${item?.name}`,
    }));
  };

  const formatPrivateNetworkOptions = (networks = []) => {
    return networks.map((network) => ({
      value: network.id,
      label: network.name,
    }));
  };

  const formatKeypairOptions = (keypairs = []) => {
    return Array.isArray(keypairs)
      ? keypairs.map(({ keypair }) => ({
        value: keypair?.name,
        label: keypair?.name,
      }))
      : [];
  };

  const keypairOptions = Array.isArray(keypairs)
    ? formatKeypairOptions(keypairs)
    : [];

  const privateNetworkOptions = formatPrivateNetworkOptions(privateNetworks);

  const ubuntuOptions = formatOptions(images.ubuntu);
  const debianOptions = formatOptions(images.debian);
  const rockyOptions = formatOptions(images.rocky);
  const centosOptions = formatOptions(images.centos);
  const opensuseOptions = formatOptions(
    images.others?.filter((item) => item.name.includes('openSUSE')) || [],
  );
  const fedoraOptions = formatOptions(images['fedora-coreos']);
  const otherUbuntuOptions = formatOptions(
    images.others?.filter((item) => item.name.includes('Ubuntu')) || [],
  );
  const otherCentosOptions = formatOptions(
    images.others?.filter((item) => item.name.includes('CentOS')) || [],
  );
  const windowsOptions = formatOptions(
    images.others?.filter((item) => item.name.includes('Windows')) || [],
  );
  const oracleOptions = formatOptions(
    images.others?.filter((item) => item.name.includes('Oracle')) || [],
  );

  const allUbuntuOptions = [...ubuntuOptions, ...otherUbuntuOptions];
  const allCentosOptions = [...centosOptions, ...otherCentosOptions];

  const getOptionsForSelectedImage = (selectedImage) => {
    switch (selectedImage) {
      case 'ubuntu':
        return allUbuntuOptions;
      case 'debian':
        return debianOptions;
      case 'rocky':
        return rockyOptions;
      case 'centos':
        return allCentosOptions;
      case 'opensuse':
        return opensuseOptions;
      case 'fedora':
        return fedoraOptions;
      case 'windows':
        return windowsOptions;
      case 'oracle':
        return oracleOptions;
      case 'snapshot':
        return location.pathname === '/client-area/configure-vps' &&
          selectedSnapshot &&
          selectedSnapshot.action === 'launch'
          ? [
            {
              value: selectedSnapshot.snapshotId,
              label: selectedSnapshot.snapshotName,
            },
          ]
          : [];
      default:
        return [];
    }
  };

  const handleImageClick = (image) => {
    if (
      location.pathname === '/client-area/configure-vps' &&
      selectedSnapshot &&
      selectedSnapshot.action === 'launch'
    )
      return;
    setSelectedImage(image);
    setSelectedValue('');
    setAuthMethod(image === 'windows' ? 'password' : 'keypairs');

    const options = getOptionsForSelectedImage(image);
    const defaultImageRef = options.length > 0 ? options[0].value : '';
    setSelectedImageRef(defaultImageRef);
    createNewServer.setFieldValue('imageRef', defaultImageRef);

    const selectedOSImage = localImageDetails?.find(
      (img) => img?.image_id === defaultImageRef,
    );
    const defaultUsername =
      image === 'windows' ? 'Administrator' : selectedOSImage?.username || '';
    createNewServer.setFieldValue('username', defaultUsername);
  };

  const createNewKeypair = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Keypair Name cannot be empty'),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { name } = values;
      let createKeypairData = { name };
      const { payload } = await dispatch(
        createKeypair({ createKeypairData, projectId: selectedProjectId }),
      );
      if (payload) {
        dispatch(listKeypairs(selectedProjectId));
        downloadPrivateKey(payload.keypair);
        resetForm();
      }
    },
  });

  const downloadPrivateKey = (keypair) => {
    const privateKey = keypair.private_key;
    const filename = `${keypair.name}.pem`;
    const blob = new Blob([privateKey], { type: 'application/x-pem-file' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const handlePaymentDuration = (duration) => {
    if (!duration) return duration;
    return duration.charAt(0).toLowerCase() + duration.slice(1);
  };

  const [selectedImageRef, setSelectedImageRef] = useState('');

  const linuxImages = [
    'fedora',
    'ubuntu',
    'rocky',
    'opensuse',
    'debian',
    'centos',
    'oracle',
  ];

  const createNewServer = useFormik({
    initialValues: {
      name: '',
      imagesRef:
        location.pathname === '/client-area/configure-vps' &&
          selectedSnapshot &&
          selectedSnapshot.action === 'launch'
          ? 'snapshot'
          : '',
      imageRef:
        location.pathname === '/client-area/configure-vps' &&
          selectedSnapshot &&
          selectedSnapshot.action === 'launch'
          ? selectedSnapshot?.snapshotId
          : '',
      old_key_pair_name: '',
      username: '',
      user_pass: '',
      confirm_pass: '',
      private_network: '',
    },
    validationSchema: Yup.lazy(() =>
      Yup.object({
        name: Yup.string().required('Instance Name cannot be empty'),
        imagesRef: Yup.string().required('Please select an image'),
        imageRef: Yup.string().required('Image version cannot be empty'),
        old_key_pair_name:
          (linuxImages.includes(selectedImage) ||
            (location.pathname === '/client-area/configure-vps' &&
              selectedSnapshot?.action === 'launch')) &&
            authMethod === 'keypairs'
            ? Yup.string().required('Key pair cannot be empty')
            : Yup.string().notRequired(),
        username:
          selectedImage === 'windows' ||
            ((linuxImages.includes(selectedImage) ||
              (location.pathname === '/client-area/configure-vps' &&
                selectedSnapshot?.action === 'launch')) &&
              authMethod === 'password')
            ? Yup.string().required('Username is required')
            : Yup.string().notRequired(),
        user_pass:
          selectedImage === 'windows' ||
            ((linuxImages.includes(selectedImage) ||
              (location.pathname === '/client-area/configure-vps' &&
                selectedSnapshot?.action === 'launch')) &&
              authMethod === 'password')
            ? Yup.string()
              .required('Password is required')
              .min(12, 'Password must be at least 12 characters long')
              .matches(
                /[A-Z]/,
                'Password must contain at least one uppercase letter',
              )
              .matches(
                /[a-z]/,
                'Password must contain at least one lowercase letter',
              )
              .matches(/[0-9]/, 'Password must contain at least one digit')
              .matches(
                /[!@#$%^&*(),.?":{}|<>]/,
                'Password must contain at least one special character',
              )
              .test(
                'no-username',
                'Password must not contain any part of the username',
                function (value) {
                  const username = this.parent.username?.trim();
                  return (
                    !username ||
                    !value ||
                    !value.toLowerCase().includes(username.toLowerCase())
                  );
                },
              )
            : Yup.string().notRequired(),
        confirm_pass:
          selectedImage === 'windows' ||
            ((linuxImages.includes(selectedImage) ||
              (location.pathname === '/client-area/configure-vps' &&
                selectedSnapshot?.action === 'launch')) &&
              authMethod === 'password')
            ? Yup.string()
              .required('Confirm Password is required')
              .oneOf([Yup.ref('user_pass'), null], 'Passwords must match')
            : Yup.string().notRequired(),
        private_network:
          networkType === 'private'
            ? Yup.string().required('Please select a private network')
            : Yup.string().notRequired(),
      }),
    ),
    onSubmit: async (values, { resetForm, setTouched }) => {
      const {
        name,
        imageRef,
        old_key_pair_name,
        volume_size,
        free_egress,
        username,
        user_pass,
        private_network,
      } = values;

      function extractNumber(str) {
        if (!str) return 0;
        const cleanedStr = str.replace(/GB/gi, '').trim();
        return parseInt(cleanedStr, 10) || 0;
      }

      const numericVolumeSize = extractNumber(volume_size);
      const numericEgressSize = extractNumber(free_egress);

      setTouched({
        name: true,
        imagesRef: true,
        imageRef: true,
        old_key_pair_name: true,
        volume_size: true,
        free_egress: true,
        username: true,
        user_pass: true,
        confirm_pass: true,
        private_network: true,
      });

      const errors = await createNewServer.validateForm();

      const fieldOrder = [
        'imagesRef',
        'imageRef',
        'name',
        'old_key_pair_name',
        'volume_size',
        'free_egress',
        'username',
        'user_pass',
        'private_network',
      ];

      const firstErrorField = fieldOrder.find((field) => errors[field]);

      if (firstErrorField && errorRefs[firstErrorField]?.current) {
        const element = errorRefs[firstErrorField].current;
        if (!element.id) {
          element.id = `error-${firstErrorField}`;
        }
        scroller.scrollTo(element.id, {
          duration: 1000,
          delay: 100,
          smooth: 'easeInOutQuart',
          offset: -100,
          containerId: 'scroll-container',
        });
        return;
      }

      const customFlavorDisc = cartItems[0].diskSize?.replace('GB', '');
      const transformCustomFlavorDisc = parseInt(customFlavorDisc);

      const customFlavorRam = cartItems[0].ramSize?.replace('GB', '');
      const transformCustomFlavorRam = parseInt(customFlavorRam);

      const selectedNetworkId =
        networkType === 'public' ? publicNetworkId : private_network;

      let createServerData;

      if (cartItems[0]?.type === 'Customize') {
        createServerData = {
          name,
          imageRef:
            location.pathname === '/client-area/configure-vps' &&
              selectedSnapshot &&
              selectedSnapshot.action === 'launch'
              ? selectedSnapshot.snapshotId
              : imageRef,
          network: selectedNetworkId,
          volume_size: numericVolumeSize || undefined,
          egress_size: numericEgressSize || undefined,
          custom_flavor_ram: transformCustomFlavorRam,
          custom_flavor_disc: transformCustomFlavorDisc,
          custom_flavor_vpcu: cartItems[0].vcpu,
          payment_method: handlePaymentDuration(duration),
        };
      } else {
        createServerData = {
          name,
          flavorRef,
          imageRef:
            location.pathname === '/client-area/configure-vps' &&
              selectedSnapshot &&
              selectedSnapshot.action === 'launch'
              ? selectedSnapshot.snapshotId
              : imageRef,
          network: selectedNetworkId,
          flavor_id: flavorId,
          volume_size: numericVolumeSize || undefined,
          egress_size: numericEgressSize || undefined,
          payment_method: handlePaymentDuration(duration),
        };
      }

      const filteredImageDetail = localImageDetails?.find(
        (imageDetail) =>
          imageDetail?.image_id ===
          (location.pathname === '/client-area/configure-vps' &&
            selectedSnapshot &&
            selectedSnapshot.action === 'launch'
            ? selectedSnapshot.snapshotId
            : imageRef),
      );

      console.log(filteredImageDetail);

      if (
        linuxImages.includes(selectedImage) &&
        !(
          location.pathname === '/client-area/configure-vps' &&
          selectedSnapshot &&
          selectedSnapshot.action === 'launch'
        )
      ) {
        if (authMethod === 'keypairs') {
          createServerData.old_key_pair_name = old_key_pair_name;
        } else if (authMethod === 'password') {
          createServerData.username = filteredImageDetail?.username || username;
          createServerData.user_pass = user_pass;
        }
      } else if (selectedImage === 'windows') {
        createServerData.username = filteredImageDetail?.username || username;
        createServerData.user_pass = user_pass;
        createServerData.server_type = 'windows';
      } else if (
        location.pathname === '/client-area/configure-vps' &&
        selectedSnapshot &&
        selectedSnapshot.action === 'launch'
      ) {
        if (authMethod === 'keypairs') {
          createServerData.old_key_pair_name = old_key_pair_name;
        } else if (authMethod === 'password') {
          createServerData.username = filteredImageDetail?.username || username;
          createServerData.user_pass = user_pass;
        }
      }

      const { payload } = await dispatch(
        createServer({ createServerData, projectId: selectedProjectId }),
      );
      const serverId = payload?.server?.id;

      if (
        payload.statuscode === '00' &&
        payload.statusmessage === 'successful'
      ) {
        setLoading(true);

        const pollForActiveStatus = async (
          serverId,
          maxRetries = 30,
          delay = 10000,
        ) => {
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            const { payload } = await dispatch(getResourceStatus({ serverId }));
            if (
              payload?.data?.status === 'active' &&
              payload?.data?.statuscode === '00'
            ) {
              setLoading(false);

              const resourceId = payload?.data?.resource_id;
              const projectId = selectedProjectId;

              const { payload: serverListPayload } = await dispatch(
                listServers(projectId),
              );

              const updatedServerList = serverListPayload
                ?.filter((server) => server.addresses)
                .map((server) => {
                  const externalAddress = server.addresses?.External?.[0]?.addr;
                  const allAddresses = Object.values(server.addresses)
                    .flat()
                    .map((address) => address.addr)
                    .join(', ');
                  return {
                    serverId: server?.id,
                    name: server.name.replace(/_/g, ' '),
                    address: externalAddress || allAddresses || '',
                    status: server.status === 'ACTIVE' ? 'Active' : 'Inactive',
                  };
                });

              const foundServer = updatedServerList?.find(
                (server) => server.serverId === resourceId,
              );

              const filteredImageDetail = localImageDetails?.find(
                (imageDetail) =>
                  imageDetail?.image_id ===
                  (location.pathname === '/client-area/configure-vps' &&
                    selectedSnapshot &&
                    selectedSnapshot.action === 'launch'
                    ? selectedSnapshot.snapshotId
                    : imageRef),
              );

              console.log(filteredImageDetail);

              setIsSuccess(true);

              const loginUsername =
                selectedImage === 'windows' || authMethod === 'password'
                  ? username
                  : location.pathname === '/client-area/configure-vps' &&
                    selectedSnapshot &&
                    selectedSnapshot.action === 'launch'
                    ? getDefaultUsername(selectedSnapshot.snapshotOS)
                    : filteredImageDetail?.username || selectedImage || 'root';

              let ruleToShow;

              if (selectedImage === 'windows' && authMethod === 'password') {
                ruleToShow =
                  networkType === 'private'
                    ? 'windows_private_password'
                    : 'windows_public_password';
              } else if (
                selectedImage !== 'windows' &&
                authMethod === 'password'
              ) {
                ruleToShow =
                  networkType === 'private'
                    ? 'linux_private_password'
                    : 'linux_public_password';
              } else if (authMethod === 'keypairs') {
                ruleToShow =
                  networkType === 'private'
                    ? 'linux_private_keypairs'
                    : 'linux_public_keypairs';
              }

              setSuccessDetails({
                username: loginUsername,
                ipAddress: foundServer?.address || 'IP not yet assigned',
                type: ruleToShow,
                password:
                  authMethod === 'password'
                    ? createNewServer.values.user_pass
                    : null, // Include password only for password auth
              });

              toast.success('Server created successfully', {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: false,
                theme: 'colored',
              });

              if (
                location.pathname === '/client-area/configure-vps' &&
                selectedSnapshot &&
                selectedSnapshot.action === 'launch'
              ) {
                sessionStorage.removeItem('selectedServerSnapshot');
                setSelectedSnapshot(null);
              }

              return;
            }
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
          setLoading(false);
        };
        await pollForActiveStatus(serverId);
      }
    },
  });

  useEffect(() => {
    createNewServer.setFieldValue('imagesRef', selectedImage || '');
  }, [selectedImage]);

  const handleVolumeChange = (e) => {
    setExtraVolumeSize(e.target.value);
    createNewServer.setFieldValue('volume_size', e.target.value);
  };

  const handleEgressChange = (e) => {
    setExtraEgressSize(e.target.value);
    createNewServer.setFieldValue('free_egress', e.target.value);
  };

  const handleCloseModal = () => {
    setIsSuccess(false);
    navigate('/client-area');
    removeFromCart();
    createNewServer.resetForm();
  };

  const handleImageVersionSelection = (option) => {
    if (
      location.pathname === '/client-area/configure-vps' &&
      selectedSnapshot &&
      selectedSnapshot.action === 'launch'
    )
      return;
    const newImageRef = option.target.value;
    setSelectedImageRef(newImageRef);
    createNewServer.setFieldValue('imageRef', newImageRef);

    const selectedOSImage = localImageDetails?.find(
      (image) => image?.image_id === newImageRef,
    );

    const defaultUsername = selectedOSImage?.username || '';
    createNewServer.setFieldValue('username', defaultUsername);
  };

  useEffect(() => {
    const errors = createNewServer.errors;
    const touched = createNewServer.touched;

    const firstErrorField = Object.keys(errors).find(
      (field) => touched[field] && errors[field],
    );

    if (
      selectedImage === null &&
      !(
        location.pathname === '/client-area/configure-vps' &&
        selectedSnapshot &&
        selectedSnapshot.action === 'launch'
      )
    ) {
      errorRefs.imagesRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else if (firstErrorField && errorRefs[firstErrorField]?.current) {
      errorRefs[firstErrorField].current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [createNewServer.errors, createNewServer.touched, selectedSnapshot]);

  const handleCopyToClipboard = (title, content) => {
    navigator.clipboard.writeText(content).then(() => {
      toast.info(`${title} content has been copied.`, {
        position: 'bottom-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        theme: 'colored',
      });
    });
  };

  const dollarPaymentLink = useFormik({
    initialValues: {
      amount: amountValue,
    },
    validationSchema: Yup.object({
      amount: Yup.string().required('Amount cannot be empty'),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { amount } = values;
      const { payload } = await dispatch(createPaymentLink({ amount }));

      const payment_link = payload?.payment_link;
      setPaymentLink(payment_link);

      if (payload.statuscode === '00') {
        setLinkGenerated(true);
        resetForm();
        setAmountValue('');
      }
    },
  });

  const handleAmountChange = (e) => {
    setAmountValue(e.target.value);
    dollarPaymentLink.setFieldValue('amount', e.target.value);
  };

  const handleProvisionClick = (e) => {
    e.preventDefault();
    // Students are not billed, so we bypass the wallet check and proceed to submit
    createNewServer.handleSubmit();
  };

  const handleConfirmPayment = async () => {
    if (!paymentConfirmed) {
      toast.error('Please confirm that you have made the payment.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: true,
        theme: 'colored',
      });
      return;
    }

    setConfirmPaymentLoading(true);
    try {
      const { payload } = await dispatch(
        getCreditValidator({ amount: totalPrice }),
      );

      if (payload && payload?.statuscode === '00') {
        const userBalance =
          payload?.pref_deno === 'naira'
            ? payload?.balance
            : payload?.dollar_balance;

        if (totalPrice > userBalance) {
          // Payment unsuccessful due to insufficient balance
          setConfirmPaymentLoading(false);
          setRetryCount((prev) => prev + 1);
          if (retryCount + 1 >= 2) {
            setShowWalletModal(false);
            setShowSupportModal(true);
          } else {
            toast.error(
              `Payment unsuccessful. Insufficient balance: ${currency === 'USD' ? 'USD' : '₦'} ${userBalance.toLocaleString()}. Required: ${currency === 'USD' ? 'USD' : '₦'} ${totalPrice.toLocaleString()}.`,
              {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: true,
                theme: 'colored',
              },
            );
          }
        } else {
          // Payment successful
          setShowWalletModal(false);
          setRetryCount(0);
          setPaymentConfirmed(false);
          setLinkGenerated(false);
          setPaymentLink('');
          toast.success('Payment confirmed successfully!', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: true,
            theme: 'colored',
          });
          // Proceed with server creation
          createNewServer.handleSubmit();
        }
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (error) {
      setRetryCount((prev) => prev + 1);
      setConfirmPaymentLoading(false);
      if (retryCount + 1 >= 2) {
        setShowWalletModal(false);
        setShowSupportModal(true);
      } else {
        toast.error('Payment verification failed. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: true,
          theme: 'colored',
        });
      }
    }
  };

  const handleSupportModalClose = () => {
    setShowSupportModal(false);
    setLinkGenerated(false);
    setPaymentLink('');
    setAmountValue('');
    navigate('/client-area/vps');
  };

  return (
    <div className="pb-10 min-h-screen">
      <p className="mb-4">Configure your VPS.</p>

      {cartItems.length > 0 ? (
        <form onSubmit={createNewServer.handleSubmit}>
          <div className="grid grid-cols-12 gap-4">
            <div
              id="scroll-container"
              ref={scrollContainerRef}
              className="col-span-12 lg:col-span-8 border dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800 rounded-md overflow-clip pb-6"
            >
              <div className="px-4 pt-5 grid md:flex justify-between items-center text-lg crimson-font">
                <span>{cartItems[0].name}</span>
                <span className="font-semibold text-[#1da1fc]">
                  {cartItems.length > 0 ? (
                    <>
                      {currency === 'USD' ? 'USD ' : '₦ '}
                      {totalPrice.toLocaleString()}
                    </>
                  ) : (
                    'No price available'
                  )}
                  <span className="text-sm font-normal text-gray-600">
                    {' '}
                    /{duration.toLowerCase()}
                  </span>
                </span>
              </div>

              <div className="p-4 border-b dark:border-b-[#c4c4c424] bg-[#c4c4c410] grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-4 text-xs mt-3">
                <div className="flex items-center gap-2">
                  <img src={cpu} alt="" className="h-4" />
                  <span>VCPUs: {cartItems[0].vcpu}</span>
                </div>
                <div className="flex items-center gap-2">
                  <img src={ram} alt="" className="h-4" />
                  <span>RAM: {cartItems[0].ramSize}</span>
                </div>
                <div className="flex items-center gap-2">
                  <img src={hdd} alt="" className="h-4" />
                  <span>Root disk: {cartItems[0].diskSize}</span>
                </div>
                <div className="flex items-center gap-2">
                  <img src={hdd} alt="" className="h-4" />
                  <span>
                    Free Egress Bandwidth: {cartItems[0].egressSize}GB
                  </span>
                </div>
              </div>

              <div className="border-b dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">1. Select term length</p>
                  <div className="flex items-center mt-2 gap-4">
                    <div
                      onClick={() => handleSelect('Monthly')}
                      className={
                        duration === 'Monthly' ? activeStyle : inactiveStyle
                      }
                    >
                      <span>Monthly</span>
                    </div>
                    <div
                      onClick={() => handleSelect('Yearly')}
                      className={
                        duration === 'Yearly' ? activeStyle : inactiveStyle
                      }
                    >
                      <span className="flex gap-3 items-center">
                        <span>Yearly</span>
                        <span className="text-[10px] bg-black text-white p-1 rounded">
                          1% discount
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-b dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <div className="w-full" ref={errorRefs.imagesRef}>
                    <p className="text-sm">2. Images</p>
                    {selectedSnapshot &&
                      selectedSnapshot.action === 'launch' ? (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Selected Snapshot: {selectedSnapshot.snapshotName}
                        </p>
                        <Select
                          name={'imageRef'}
                          options={[
                            {
                              value: selectedSnapshot.snapshotId,
                              label: selectedSnapshot.snapshotName,
                            },
                          ]}
                          value={createNewServer.values.imageRef}
                          disabled={
                            location.pathname ===
                            '/client-area/configure-vps' &&
                            selectedSnapshot &&
                            selectedSnapshot.action === 'launch'
                          }
                          onError={
                            createNewServer.errors.imageRef &&
                              createNewServer.touched.imageRef
                              ? createNewServer.errors.imageRef
                              : null
                          }
                        />
                      </div>
                    ) : (
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'ubuntu'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('ubuntu')}
                        >
                          <div className="flex justify-center mb-4">
                            <img src={ubuntu} alt="Ubuntu" className="h-10" />
                          </div>
                          <p className="text-center">Ubuntu</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'debian'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('debian')}
                        >
                          <div className="flex justify-center mb-4">
                            <img src={debian} alt="Debian" className="h-10" />
                          </div>
                          <p className="text-center">Debian</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'rocky'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('rocky')}
                        >
                          <div className="flex justify-center mb-4">
                            <img src={rocky} alt="Rocky" className="h-10" />
                          </div>
                          <p className="text-center">Rocky</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'centos'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('centos')}
                        >
                          <div className="flex justify-center mb-4">
                            <img src={centOS} alt="CentOS" className="h-10" />
                          </div>
                          <p className="text-center">CentOS</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'oracle'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('oracle')}
                        >
                          <div className="flex justify-center mb-4">
                            <img
                              src={oracle}
                              alt="Oracle Linux"
                              className="h-10"
                            />
                          </div>
                          <p className="text-center">Oracle Linux</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'opensuse'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('opensuse')}
                        >
                          <div className="flex justify-center mb-4">
                            <img
                              src={openSUSE}
                              alt="openSUSE"
                              className="h-10"
                            />
                          </div>
                          <p className="text-center">openSUSE</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'fedora'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('fedora')}
                        >
                          <div className="flex justify-center mb-4">
                            <img src={fedora} alt="Fedora" className="h-10" />
                          </div>
                          <p className="text-center">Fedora</p>
                        </div>
                        {parseInt(cartItems[0]?.ramSize?.replace(/\D/g, '')) >=
                          4 &&
                          parseInt(
                            cartItems[0]?.diskSize?.replace(/\D/g, ''),
                          ) >= 20 && (
                            <div
                              className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'windows'
                                ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                                : ''
                                }`}
                              onClick={() => handleImageClick('windows')}
                            >
                              <div className="flex justify-center mb-4">
                                <img
                                  src={windows}
                                  alt="Windows"
                                  className="h-10"
                                />
                              </div>
                              <p className="text-center">Windows</p>
                            </div>
                          )}
                      </div>
                    )}
                    {createNewServer.touched.imagesRef &&
                      createNewServer.errors.imagesRef && (
                        <span className="text-red-500 text-xs">
                          {createNewServer.errors.imagesRef}
                        </span>
                      )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-10">
                    {selectedImage && (
                      <div ref={errorRefs.imageRef}>
                        <p className="capitalize">{selectedImage}</p>
                        <Select
                          name={'imageRef'}
                          options={[
                            {
                              value: '',
                              label: 'Select Image',
                              disabled: true,
                            },
                            ...getOptionsForSelectedImage(selectedImage),
                          ]}
                          value={createNewServer.values.imageRef}
                          onChange={(option) =>
                            handleImageVersionSelection(option)
                          }
                          disabled={
                            selectedSnapshot &&
                            selectedSnapshot.action === 'launch'
                          }
                          onError={
                            createNewServer.errors.imageRef &&
                              createNewServer.touched.imageRef
                              ? createNewServer.errors.imageRef
                              : null
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-b dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">3. Instance name</p>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2">
                    <div ref={errorRefs.name}>
                      <Input
                        label={'Name'}
                        type={'text'}
                        name={'name'}
                        value={createNewServer.values.name}
                        onBlur={createNewServer.handleBlur}
                        onChange={createNewServer.handleChange}
                        onError={
                          createNewServer.errors.name &&
                            createNewServer.touched.name
                            ? createNewServer.errors.name
                            : null
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-b dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">4. Authentication Method</p>

                  {(linuxImages.includes(selectedImage) ||
                    (location.pathname === '/client-area/configure-vps' &&
                      selectedSnapshot?.action === 'launch')) && (
                      <div className="mt-4">
                        <div className="flex flex-col items-start md:flex-row gap-4 mb-4">
                          <label
                            className={`flex items-start gap-3 p-4 min-h-24 ${authMethod === 'keypairs' && 'border border-primaryBlue'} rounded-lg cursor-pointer w-full md:w-1/2 hover:border-primaryBlue hover:border`}
                          >
                            <input
                              type="radio"
                              name="authMethod"
                              value="keypairs"
                              checked={authMethod === 'keypairs'}
                              onChange={() => setAuthMethod('keypairs')}
                              className="mt-1 w-5 h-5 text-primaryBlue border-gray-300 focus:ring-primaryBlue"
                            />
                            <div className="flex flex-col">
                              <span className="text-base font-medium text-gray-900 dark:text-white">
                                SSH Keys
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                A more secure authentication method
                              </span>
                            </div>
                          </label>
                          <label
                            className={`flex items-start gap-3 p-4 min-h-24 ${authMethod === 'password' && 'border border-primaryBlue'} rounded-lg cursor-pointer w-full md:w-1/2 hover:border-primaryBlue hover:border`}
                          >
                            <input
                              type="radio"
                              name="authMethod"
                              value="password"
                              checked={authMethod === 'password'}
                              onChange={() => setAuthMethod('password')}
                              className="mt-1 w-5 h-5 text-primaryBlue border-gray-300 focus:ring-primaryBlue"
                            />
                            <div className="flex flex-col">
                              <span className="text-base font-medium text-gray-900 dark:text-white">
                                Password
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                Create a password to access dew (less secure)
                              </span>
                            </div>
                          </label>
                        </div>
                      </div>
                    )}

                  {selectedImage && (
                    <div className="mt-4">
                      {(linuxImages.includes(selectedImage) ||
                        (selectedSnapshot &&
                          selectedSnapshot.action === 'launch')) &&
                        authMethod === 'keypairs' && (
                          <>
                            <div className="flex divide-x text-sm mb-4">
                              <div
                                className={`cursor-pointer pe-4 ${selectedOption === 'existing'
                                  ? 'text-black dark:text-white'
                                  : 'text-[#c4c4c4] dark:text-[#c4c4c450]'
                                  }`}
                                onClick={() => setSelectedOption('existing')}
                              >
                                Select from existing keys
                              </div>
                              <div
                                className={`cursor-pointer ps-4 ${selectedOption === 'new'
                                  ? 'text-black dark:text-white'
                                  : 'text-[#c4c4c4] dark:text-[#c4c4c450]'
                                  }`}
                                onClick={() => setSelectedOption('new')}
                              >
                                Create new key pair
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 items-end">
                              {selectedOption === 'existing' && (
                                <div
                                  className="mt-4"
                                  ref={errorRefs.old_key_pair_name}
                                >
                                  <Select
                                    name={'old_key_pair_name'}
                                    options={[
                                      {
                                        value: '',
                                        label: 'Select Keypair',
                                        disabled: true,
                                      },
                                      ...keypairOptions,
                                    ]}
                                    value={
                                      createNewServer.values.old_key_pair_name
                                    }
                                    onChange={(option) =>
                                      createNewServer.setFieldValue(
                                        'old_key_pair_name',
                                        option.target.value,
                                      )
                                    }
                                    onError={
                                      createNewServer.errors
                                        .old_key_pair_name &&
                                        createNewServer.touched.old_key_pair_name
                                        ? createNewServer.errors
                                          .old_key_pair_name
                                        : null
                                    }
                                  />
                                </div>
                              )}
                              {selectedOption === 'new' && (
                                <div className="grid gap-4">
                                  <div
                                    className="grid gap-4 mt-8"
                                    ref={errorRefs.keypair_name}
                                  >
                                    <Input
                                      label={'Name'}
                                      type={'text'}
                                      name={'name'}
                                      value={createNewKeypair.values.name}
                                      onBlur={createNewKeypair.handleBlur}
                                      onChange={createNewKeypair.handleChange}
                                      onError={
                                        createNewKeypair.errors.name &&
                                          createNewKeypair.touched.name
                                          ? createNewKeypair.errors.name
                                          : null
                                      }
                                    />
                                    <div className="grid">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          createNewKeypair.handleSubmit(e);
                                        }}
                                        className="text-sm md:text-xs opacity-70 text-white bg-[#1DA1F2] rounded px-8 py-4 cursor-pointer"
                                      >
                                        Create Key
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}

                      {(selectedImage === 'windows' ||
                        ((linuxImages.includes(selectedImage) ||
                          (selectedSnapshot &&
                            selectedSnapshot.action === 'launch')) &&
                          authMethod === 'password')) && (
                          <div className="mt-4 grid gap-4">
                            <div ref={errorRefs.username}>
                              <Input
                                label={'Username'}
                                type={'text'}
                                name={'username'}
                                value={createNewServer.values.username}
                                onBlur={createNewServer.handleBlur}
                                onChange={createNewServer.handleChange}
                                onError={
                                  createNewServer.errors.username &&
                                    createNewServer.touched.username
                                    ? createNewServer.errors.username
                                    : null
                                }
                              />
                            </div>
                            <div ref={errorRefs.user_pass}>
                              <Input
                                label={'Password'}
                                type={'password'}
                                isPassword={'true'}
                                name={'user_pass'}
                                value={createNewServer.values.user_pass}
                                onBlur={createNewServer.handleBlur}
                                onChange={createNewServer.handleChange}
                                onError={
                                  createNewServer.errors.user_pass &&
                                    createNewServer.touched.user_pass
                                    ? createNewServer.errors.user_pass
                                    : null
                                }
                              />
                              <Input
                                label={'Confirm Password'}
                                type={'password'}
                                isPassword={'true'}
                                name={'confirm_pass'}
                                value={createNewServer.values.confirm_pass}
                                onBlur={createNewServer.handleBlur}
                                onChange={createNewServer.handleChange}
                                onError={
                                  createNewServer.errors.confirm_pass &&
                                    createNewServer.touched.confirm_pass
                                    ? createNewServer.errors.confirm_pass
                                    : null
                                }
                              />
                              <div className="px-6 mt-2 pb-4 text-xs text-black/50 dark:text-white/50">
                                <p className="mb-2">Password must contain:</p>
                                <ul className="list-inside list-disc gap-2 grid">
                                  <li>At least 12 characters</li>
                                  <li>At least one uppercase letter</li>
                                  <li>At least one lowercase letter</li>
                                  <li>At least one digit</li>
                                  <li>At least one special character</li>
                                  <li>
                                    Must not contain any part of the username
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-b dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">5. Network Configuration</p>
                  <div className="mt-4">
                    <div className="flex flex-col items-start md:flex-row gap-4 mb-4">
                      <label
                        className={`flex items-start gap-3 p-4 min-h-24 ${networkType === 'public' &&
                          'border border-primaryBlue'
                          } rounded-lg cursor-pointer w-full md:w-1/2 hover:border-primaryBlue hover:border`}
                      >
                        <input
                          type="radio"
                          name="networkType"
                          value="public"
                          checked={networkType === 'public'}
                          onChange={() => {
                            setNetworkType('public');
                            setSelectedPrivateNetwork('');
                            createNewServer.setFieldValue(
                              'private_network',
                              '',
                            );
                          }}
                          className="mt-1 w-5 h-5 text-primaryBlue border-gray-300 focus:ring-primaryBlue"
                        />
                        <div className="flex flex-col">
                          <span className="text-base font-medium text-gray-900 dark:text-white">
                            Public Network
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Connect to the external network
                          </span>
                        </div>
                      </label>
                      <label
                        className={`flex items-start gap-3 p-4 min-h-24 ${networkType === 'private' &&
                          'border border-primaryBlue'
                          } rounded-lg cursor-pointer w-full md:w-1/2 hover:border-primaryBlue hover:border`}
                      >
                        <input
                          type="radio"
                          name="networkType"
                          value="private"
                          checked={networkType === 'private'}
                          onChange={() => setNetworkType('private')}
                          className="mt-1 w-5 h-5 text-primaryBlue border-gray-300 focus:ring-primaryBlue"
                        />
                        <div className="flex flex-col">
                          <span className="text-base font-medium text-gray-900 dark:text-white">
                            Private Network
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Select from your private networks
                          </span>
                        </div>
                      </label>
                    </div>
                    {networkType === 'private' && (
                      <div className="mt-4" ref={errorRefs.private_network}>
                        <Select
                          name={'private_network'}
                          options={[
                            {
                              value: '',
                              label: 'Select Private Network',
                              disabled: true,
                            },
                            ...privateNetworkOptions,
                          ]}
                          value={createNewServer.values.private_network}
                          onChange={(option) => {
                            setSelectedPrivateNetwork(option.target.value);
                            createNewServer.setFieldValue(
                              'private_network',
                              option.target.value,
                            );
                          }}
                          onError={
                            createNewServer.errors.private_network &&
                              createNewServer.touched.private_network
                              ? createNewServer.errors.private_network
                              : null
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">6. Extra storage (Optional)</p>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2">
                    <div ref={errorRefs.volume_size}>
                      <Input
                        type="text"
                        placeholder={`e.g ${storage}`}
                        name={'volume_size'}
                        value={extraVolumeSize}
                        onBlur={createNewServer.handleBlur}
                        onChange={handleVolumeChange}
                        onError={
                          createNewServer.errors.volume_size &&
                            createNewServer.touched.volume_size
                            ? createNewServer.errors.volume_size
                            : null
                        }
                      />
                      {storage && (
                        <span className="text-gray-500 text-xs mt-2">
                          Extra storage must not exceed {storage}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* <div className="dark:text-white bg-white dark:bg-slate-800">

              {/* <div className="dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">7. Extra Free Egress Bandwidth (Optional)</p>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2">
                    <div ref={errorRefs.free_egress}>
                      <Input
                        type="text"
                        placeholder={`e.g ${freeEgress}GB`}
                        name={"free_egress"}
                        value={extraEgressSize}
                        onBlur={createNewServer.handleBlur}
                        onChange={handleEgressChange}
                        onError={
                          createNewServer.errors.free_egress && createNewServer.touched.free_egress
                            ? createNewServer.errors.free_egress
                            : null
                        }
                      />
                      {freeEgress && (
                        <span className="text-gray-500 text-xs mt-2">
                          Extra free egress bandwidth must not exceed {freeEgress}GB
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div> */}
            </div>

            <div className="col-span-12 lg:col-span-4 relative">
              <div className="bg-[#1da1f2] rounded text-white md:sticky md:top-24">
                <div className="border-b border-white/30 p-5">
                  <span className="font-bold crimson-font"> Summary </span>
                </div>
                <div className="p-4 text-sm grid gap-4">
                  <div className="grid grid-cols-2 gap-1">
                    <p>Items:</p>
                    <p className="font-semibold">{cartItems.length}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <p>Selected dew:</p>
                    <p className="font-semibold">{cartItems[0].name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <p>Term length:</p>
                    <p className="font-semibold">{duration}</p>
                  </div>
                  {selectedSnapshot && selectedSnapshot.action === 'launch' && (
                    <div className="grid grid-cols-2 gap-1">
                      <p>Selected Snapshot:</p>
                      <p className="font-semibold">
                        {selectedSnapshot.snapshotName}
                      </p>
                    </div>
                  )}
                  {extraVolumeSize && (
                    <div className="grid grid-cols-2 gap-1">
                      <p>Extra volume:</p>
                      <p className="font-semibold">{extraVolumeSize}</p>
                    </div>
                  )}
                  {extraVolumeSize && (
                    <div className="grid grid-cols-2 gap-1">
                      <p>Extra Volume Cost:</p>
                      <p className="font-semibold">
                        {currency === 'USD' ? 'USD ' : '₦ '}
                        {calculateExtraVolumeCost(
                          extraVolumeSize,
                          currency,
                        ).toLocaleString()}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-1">
                    <p>Amount:</p>
                    <p className="font-semibold">
                      {currency === 'USD' ? 'USD ' : '₦ '}
                      {totalPrice.toLocaleString()}
                    </p>
                  </div>
                  <div className="mt-4 grid gap-2">
                    <button
                      className={`text-primaryBlue bg-white rounded p-4 text-xs w-full ${createNewServer.isSubmitting || loading
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                        }`}
                      type="submit"
                      onClick={handleProvisionClick}
                      disabled={createNewServer.isSubmitting || loading}
                    >
                      Provision
                    </button>
                    <div
                      onClick={removeFromCart}
                      className={`text-center cursor-pointer text-xs p-4 rounded hover:bg-red-500 transition-all ${createNewServer.isSubmitting || loading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      style={createNewServer.isSubmitting || loading ? { pointerEvents: "none" } : {}}
                    >
                      Remove
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="flex justify-center items-center h-[400px]">
          <div className="text-center grid gap-2">
            <p className="text-[#1da1f2] text-xl font-medium crimson-font">
              You haven't selected a VPS to configure
            </p>
            <Link
              to={generatePartnerUrl("/client-area/vps")}
              className="dark:text-white/50 text-black/50 text-sm underline"
            >
              Choose and buy a Flavour
            </Link>
          </div>
        </div>
      )}
      {loading && (
        <div>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading}
          >
            <CircularProgress color="inherit" />
            <p className="ml-3">Creating server...</p>
          </Backdrop>
        </div>
      )}

      {isSuccess && (
        <div className="fixed w-full bg-black/60 top-0 left-0 z-50 h-screen">
          <div className="flex h-full items-center justify-center p-4">
            <div className="bg-white dark:bg-[#141D26] dark:text-white w-full md:w-[40%] rounded max-h-[90%] overflow-auto">
              <div className="px-6 md:px-10 py-6 border-b dark:border-b-[#c4c4c432] flex justify-between items-center">
                <span className="text-primaryBlue font-medium">
                  {successDetails.type.includes('windows')
                    ? networkType === 'private'
                      ? 'To access your instance'
                      : 'Use Remote Desktop (RDP) to connect'
                    : 'To login to your instance'}
                </span>
                <span onClick={handleCloseModal} className="cursor-pointer">
                  <img src={closeIcon} alt="Close" className="h-5" />
                </span>
              </div>
              <div className="py-10 grid gap-4">
                {/* Private Network - Windows */}
                {successDetails.type === 'windows_private_password' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="bg-gray-300 p-4 rounded-lg">
                      <h4 className="text-gray-950 font-medium text-sm">
                        To access your instance, you must first attach a
                        floating IP to it.
                      </h4>
                    </div>
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm mb-3">
                          Once a floating IP has been attached:
                        </h4>
                        <h4 className="text-gray-950 font-medium text-sm mb-3">
                          Your Remote Desktop (RDP) credentials to connect with:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          Username: {successDetails.username}
                        </h4>
                        {/* <h4 className="text-gray-950 font-bold text-lg">
                          Password: {successDetails.password}
                        </h4> */}
                        <h4 className="text-gray-950 font-bold text-lg">
                          IP Address:{' '}
                          {successDetails.floatingIp || '<floating-ip>'}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'RDP Credentials',
                            `Username: ${successDetails.username}\nPassword: ${successDetails.password}\nIP Address: ${successDetails.ipAddress}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Public Network - Windows */}
                {successDetails.type === 'windows_public_password' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm mb-3">
                          Your Remote Desktop (RDP) credentials to connect with:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          Username: {successDetails.username}
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          IP Address: {successDetails.ipAddress}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'RDP Credentials',
                            `Username: ${successDetails.username}\nPassword: ${successDetails.password}\nIP Address: ${successDetails.ipAddress}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Private Network - Linux Password */}
                {successDetails.type === 'linux_private_password' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="bg-gray-300 p-4 rounded-lg">
                      <h4 className="text-gray-950 font-medium text-sm">
                        To access your instance, you must first attach a
                        floating IP to it.
                      </h4>
                    </div>
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm mb-3">
                          Once a floating IP has been attached, login into your
                          instance by running the command:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          ssh {successDetails.username}@
                          {successDetails.floatingIp || '<floating-ip>'}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'SSH Credentials',
                            `ssh ${successDetails.username}@${successDetails.floatingIp || '<floating-ip>'}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Public Network - Linux Password */}
                {successDetails.type === 'linux_public_password' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm mb-3">
                          Login to your instance by running the command:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          ssh {successDetails.username}@
                          {successDetails.ipAddress}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'SSH Credentials',
                            `ssh ${successDetails.username}@${successDetails.ipAddress}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Private Network - Linux Keypairs */}
                {successDetails.type === 'linux_private_keypairs' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="w-full">
                      <h4 className="text-gray-950 font-medium text-sm mb-3">
                        To access your instance, you must first attach a
                        floating IP to it. Once a floating IP has been attached:
                      </h4>
                    </div>
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm">
                          1. Change the permission of your private key:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          chmod 400 /path/to/privatekey.pem
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'Private Key Permissions',
                            'chmod 400 /path/to/privatekey.pem',
                          )
                        }
                      />
                    </div>
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm">
                          2. Login to your instance by running the command:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          ssh -i /path/to/privatekey.pem{' '}
                          {successDetails.username}@
                          {successDetails.floatingIp || '<floating-ip>'}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'SSH Login',
                            `ssh -i /path/to/privatekey.pem ${successDetails.username}@${successDetails.floatingIp || '<floating-ip>'}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Public Network - Linux Keypairs */}
                {successDetails.type === 'linux_public_keypairs' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm">
                          1. Change the permission of your private key:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          chmod 400 /path/to/privatekey.pem
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'Private Key Permissions',
                            'chmod 400 /path/to/privatekey.pem',
                          )
                        }
                      />
                    </div>
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm">
                          2. Login to your instance by running the command:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          ssh -i /path/to/privatekey.pem{' '}
                          {successDetails.username}@{successDetails.ipAddress}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'SSH Login',
                            `ssh -i /path/to/privatekey.pem ${successDetails.username}@${successDetails.ipAddress}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                <div className="px-6 md:px-10 flex justify-end">
                  <button
                    type="button"
                    className="text-sm rounded text-white bg-[#1DA1F2] px-8 py-4 cursor-pointer"
                    onClick={handleCloseModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showWalletModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-lg w-full max-w-md max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center p-6 border-b border-[#c4c4c432] dark:border-[#c4c4c432]">
              <h2 className="text-sm font-medium text-primaryBlue">
                {currency === 'USD' ? 'Dollar' : 'Naira'} Account Details
              </h2>
              <img
                src={close}
                onClick={() => setShowWalletModal(false)}
                className="h-5 cursor-pointer"
                alt="Close"
              />
            </div>
            <div className="p-6 text-sm grid gap-4">
              {virtualAccount ? (
                <>
                  {currency === 'USD' ? (
                    linkGenerated ? (
                      <div className="grid gap-4">
                        <input
                          type="text"
                          value={paymentLink}
                          disabled
                          className="w-full p-3 border border-[#c4c4c450] dark:border-[#c4c4c416] rounded-md bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300"
                        />
                        {paymentLink && (
                          <a
                            href={paymentLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <button
                              className="w-full text-white bg-primaryBlue rounded p-4 text-xs hover:bg-blue-600"
                              onClick={() => setShowWalletModal(false)}
                            >
                              Make Payment
                            </button>
                          </a>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="grid gap-2">
                          <p className="font-semibold">
                            OPTION 1: Generate Payment Link
                          </p>
                          <div>
                            <input
                              type="text"
                              placeholder="Enter amount"
                              value={amountValue}
                              onChange={handleAmountChange}
                              onBlur={dollarPaymentLink.handleBlur}
                              className="w-full p-3 border border-[#c4c4c450] dark:border-[#c4c4c416] rounded-md bg-white dark:bg-[#1f2a44] text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primaryBlue"
                            />
                            {dollarPaymentLink.errors.amount &&
                              dollarPaymentLink.touched.amount && (
                                <span className="text-red-500 text-xs mt-1">
                                  {dollarPaymentLink.errors.amount}
                                </span>
                              )}
                          </div>
                          <button
                            className="w-full text-white bg-primaryBlue rounded p-4 text-xs hover:bg-blue-600"
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              dollarPaymentLink.handleSubmit(e);
                            }}
                          >
                            Generate Payment Link
                          </button>
                        </div>
                        <div className="grid gap-2 mt-5 border-t border-[#c4c4c432] pt-5">
                          <p className="font-semibold">
                            OPTION 2: Bank Transfer
                          </p>
                          <p>
                            Transfer the exact amount to the account below and
                            call{' '}
                            <a
                              href="tel:09069434255"
                              className="text-blue-500 underline"
                            >
                              09069434255
                            </a>{' '}
                            or send a mail with receipt of payment to{' '}
                            <a
                              href="mailto:support@cloudvantage.com.ng"
                              className="text-blue-500 underline"
                            >
                              support@cloudvantage.com.ng
                            </a>
                            .
                          </p>
                          <p>
                            <span className="font-semibold">Bank name:</span>{' '}
                            Guaranty Trust Bank
                          </p>
                          <p>
                            <span className="font-semibold">
                              Account number:
                            </span>{' '}
                            0891825462
                          </p>
                          <p>
                            <span className="font-semibold">Account name:</span>{' '}
                            Cloudvantage Limited
                          </p>
                        </div>
                      </>
                    )
                  ) : (
                    <div className="grid gap-2">
                      <p className="font-semibold mb-5">
                        Fund your wallet by making a bank transfer to account
                        details below:
                      </p>
                      <p>
                        <span className="font-semibold">Bank name:</span>{' '}
                        Providus Bank
                      </p>
                      <p>
                        <span className="font-semibold">Account number:</span>{' '}
                        {virtualAccount.acct_num || ''}
                      </p>
                      <p>
                        <span className="font-semibold">Account name:</span>{' '}
                        {virtualAccount.acct_name || ''}
                      </p>
                    </div>
                  )}
                  {!linkGenerated && (
                    <>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={paymentConfirmed}
                          disabled={confirmPaymentLoading}
                          onChange={(e) =>
                            setPaymentConfirmed(e.target.checked)
                          }
                          className="h-5 w-5 text-primaryBlue border-gray-300 rounded focus:ring-primaryBlue"
                        />
                        <span>I can confirm I already made payment</span>
                      </label>
                      <button
                        className={`w-full text-white rounded p-4 text-xs flex justify-center items-center ${retryCount >= 2 || confirmPaymentLoading
                          ? 'bg-gray-500 cursor-not-allowed'
                          : 'bg-primaryBlue hover:bg-blue-600'
                          }`}
                        onClick={handleConfirmPayment}
                        disabled={retryCount >= 2 || confirmPaymentLoading}
                      >
                        {confirmPaymentLoading ? (
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            ></path>
                          </svg>
                        ) : retryCount > 0 ? (
                          'Retry Payment'
                        ) : (
                          'Confirm Payment'
                        )}
                      </button>
                    </>
                  )}
                </>
              ) : (
                <p>No account details available.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Support Contact Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-lg w-full max-w-md">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-primaryBlue mb-4">
                Payment Verification Failed
              </h2>
              <p className="text-sm">
                We could not verify your payment. Please contact our support
                team at{' '}
                <a href="tel:09069434255" className="text-blue-500 underline">
                  09069434255
                </a>{' '}
                or email{' '}
                <a
                  href="mailto:support@cloudvantage.com.ng"
                  className="text-blue-500 underline"
                >
                  support@cloudvantage.com.ng
                </a>{' '}
                to confirm your payment.
              </p>
              <div className="flex justify-end mt-4">
                <button
                  className="text-white bg-primaryBlue rounded p-4 text-xs hover:bg-blue-600"
                  onClick={handleSupportModalClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigureVPS;
